import { ApiError, api } from './api.ts';

/**
 * MapKit JS bootstrap — the frozen dashboard's PRIMARY map engine (evac-evidence
 * `docs/design/design-status.md`: Apple ideally; Google Maps is the fallback,
 * and an honest "unavailable" panel the floor — see `map.tsx`).
 *
 * The choice is made once per session and it degrades, never breaks:
 *
 * 1. Ask our API for a token. 501 means "not configured" (the provider gate
 *    is off, or the key is not in Secrets Manager yet) — fall through to Google.
 * 2. Load Apple's script. If the CSP has not been extended yet, or the CDN
 *    is unreachable/stalled, the load fails or times out — fall through to Google.
 * 3. Initialise mapkit with a callback that re-fetches a fresh token when
 *    Apple asks (tokens live 30 minutes; MapKit calls back on expiry).
 *
 * Every failure path lands on the next engine, because a map that is grey on a
 * live incident screen is not a fallback story, it is an outage.
 */

declare global {
  interface Window {
    mapkit?: typeof mapkit;
  }
  // Minimal surface of the MapKit JS API this codebase touches. Apple ships
  // no official types for the CDN build; declaring only what we use keeps the
  // compiler honest without a 3000-line vendored declaration. A namespace is
  // the one construct that can type a script-injected global with nested
  // classes, so the lint rule is disabled for exactly this block.
  // eslint-disable-next-line @typescript-eslint/no-namespace
  namespace mapkit {
    function init(options: {
      authorizationCallback: (done: (token: string) => void) => void;
    }): void;
    const FeatureVisibility: { Hidden: string; Visible: string; Adaptive: string };
    // Map declutter (`docs/design-dashboard.md` §8b/§8c): streets and place/
    // suburb names only, no POI icons. MapKit JS exposes the static factories
    // `including(categories)` / `excluding(categories)` — NOT the native-only
    // `filterExcludingAllCategories` constant, which is absent here (see
    // `excludeAllPoiFilter` in `map-mapkit.tsx`). `including([])` includes no
    // categories, i.e. hides every POI; the Google fallback matches it with a
    // styles array. `categories` are `PointOfInterestCategory` values, typed as
    // the minimal shape this codebase reads.
    const PointOfInterestFilter: {
      including(categories: string[]): unknown;
      excluding(categories: string[]): unknown;
    };
    class Coordinate {
      constructor(latitude: number, longitude: number);
      latitude: number;
      longitude: number;
    }
    class CoordinateSpan {
      constructor(latitudeDelta: number, longitudeDelta: number);
    }
    class CoordinateRegion {
      constructor(center: Coordinate, span: CoordinateSpan);
    }
    class Annotation {
      constructor(
        coordinate: Coordinate,
        factory: (coordinate: Coordinate, options: unknown) => Element,
        options?: { anchorOffset?: DOMPoint },
      );
      // Read-write: assigning a new Coordinate moves the annotation in place
      // (MapKit animates it), which is how a marker travels without the map
      // being rebuilt — the live-position path in `map-mapkit.tsx`.
      coordinate: Coordinate;
      // Assigning true lets the person pick the pin up; `drag-end` fires with
      // the annotation's coordinate already updated (the wizard's move path).
      // Optional in the decl so MarkerAnnotation (whose sites never touch it)
      // still satisfies the Annotation-typed slots it is assigned into.
      draggable?: boolean;
      // `select`/`deselect` fire when the annotation is tapped — the marker-tap
      // hook the Home popover rides on, mirrored by the Google build's DOM click.
      // `drag-start`/`dragging`/`drag-end` ride the same signature.
      addEventListener(type: string, listener: (event: unknown) => void): void;
    }
    // MapKit's stock red pin. Preferred over a custom-drawn factory when a
    // caller just wants the platform's own pin — a preview map beside an
    // address field, say, where the point IS the answer.
    class MarkerAnnotation {
      constructor(
        coordinate: Coordinate,
        options?: { color?: string; glyphText?: string; title?: string },
      );
      coordinate: Coordinate;
      // Same tap hook as Annotation — a MarkerAnnotation is a MapKit annotation
      // too, so it carries `select`/`deselect`. Declared here so a MarkerAnnotation
      // is structurally assignable where an Annotation is expected (addAnnotation).
      addEventListener(type: string, listener: (event: unknown) => void): void;
    }
    class Map {
      constructor(
        element: Element,
        options?: {
          colorScheme?: string;
          showsCompass?: string;
          showsMapTypeControl?: boolean;
          isRotationEnabled?: boolean;
          /** Declutter: a PointOfInterestFilter that includes no categories drops every POI icon (see PointOfInterestFilter, and `excludeAllPoiFilter`). */
          pointOfInterestFilter?: unknown;
        },
      );
      static ColorSchemes: { Light: string; Dark: string };
      colorScheme: string;
      /** Standard cartography, or the satellite imagery (Hybrid keeps road labels). */
      static MapTypes: {
        Standard: string;
        MutedStandard: string;
        Hybrid: string;
        Satellite: string;
      };
      mapType: string;
      /**
       * Read-write on the instance, and the assignment that actually declutters
       * — MapKit JS ignores the same key passed as a construction option, so the
       * POI filter is set on the live map after creation (see `map-mapkit.tsx`).
       */
      pointOfInterestFilter: unknown;
      addAnnotations(annotations: Annotation[]): void;
      removeAnnotations(annotations: Annotation[]): void;
      addOverlay(overlay: CircleOverlay | PolygonOverlay | PolylineOverlay): void;
      removeOverlay(overlay: CircleOverlay | PolygonOverlay | PolylineOverlay): void;
      /** Camera-affecting insets: framing and regions centre in the unpadded area. */
      padding: Padding;
      showItems(items: Annotation[], options?: { animate?: boolean; padding?: unknown }): void;
      setRegionAnimated(region: CoordinateRegion, animate?: boolean): void;
      addEventListener(type: string, listener: (event: unknown) => void): void;
      convertPointOnPageToCoordinate(point: DOMPoint): Coordinate;
      convertCoordinateToPointOnPage(coordinate: Coordinate): DOMPoint;
      element: Element;
      destroy(): void;
    }
    class Padding {
      constructor(insets: { top: number; right: number; bottom: number; left: number });
    }
    /** Overlay paint. MapKit JS applies it via the overlay's `style` property. */
    class Style {
      constructor(options?: {
        lineWidth?: number;
        strokeColor?: string;
        strokeOpacity?: number;
        fillColor?: string;
        fillOpacity?: number;
      });
    }
    /**
     * A radius on the map — the geofence/assembly-area circles (migration
     * 0111). Constructed with a RADIUS in metres; added via `addOverlay`.
     */
    class CircleOverlay {
      constructor(coordinate: Coordinate, radius: number, options?: { style?: Style });
      coordinate: Coordinate;
      radius: number;
      style: Style;
    }
    // The drawn-area sibling (migration 0123). MapKit closes the ring itself.
    class PolygonOverlay {
      constructor(points: Coordinate[], options?: { style?: Style });
      points: Coordinate[];
      style: Style;
    }
    // A drawn area's first two corners — a line before it is an area.
    class PolylineOverlay {
      constructor(points: Coordinate[], options?: { style?: Style });
      points: Coordinate[];
      style: Style;
    }
    interface AutocompleteResult {
      coordinate?: Coordinate;
      // The human-readable rendering Apple returns — line 0 is the place name
      // ("Lincoln Primary"), line 1 is the address ("100 Collins St, Melbourne
      // VIC 3000"). Prefer this over reconstructing from structuredAddress
      // because it is already localised and formatted per country.
      displayLines: string[];
      structuredAddress?: {
        subThoroughfare?: string;
        thoroughfare?: string;
        subLocality?: string;
        locality?: string;
        subAdministrativeArea?: string;
        administrativeArea?: string;
        postCode?: string;
        country?: string;
      };
    }
    /** Coordinate → address, the reverse of `Search` (the wizard shows a
     *  dropped pin's address with it). */
    class Geocoder {
      constructor(options?: { language?: string });
      reverseLookup(
        coordinate: Coordinate,
        callback: (
          err: Error | null,
          data: { results: { formattedAddress?: string; name?: string }[] },
        ) => void,
      ): number;
    }
    class Search {
      constructor(options?: {
        region?: CoordinateRegion;
        // `Default` BIASES results toward the region; `Required` restricts to
        // it. Address search uses Default — a hint, never a fence.
        regionPriority?: unknown;
        language?: string;
        getsUserLocation?: boolean;
      });
      // Optional on the decl: older MapKit builds lack it, and the caller
      // falls back to the constructor's region alone.
      static RegionPriority?: { Default: unknown; Required: unknown };
      // Returns a numeric request id; cancel by handing it back to `cancel(id)`
      // on the same Search instance. (The old decl claimed it returned a
      // `{ cancel() }` object, which never existed and crashed the caller.)
      autocomplete(
        query: string,
        callback: (err: Error | null, data: { results: AutocompleteResult[] }) => void,
        options?: { region?: CoordinateRegion; language?: string },
      ): number;
      // A full search — used to resolve a picked autocomplete row that came
      // back without a coordinate (an address row often does).
      search(
        query: string | AutocompleteResult,
        callback: (err: Error | null, data: { places?: { coordinate?: Coordinate }[] }) => void,
        options?: { region?: CoordinateRegion },
      ): number;
      cancel(id: number): boolean;
    }
  }
}

const SCRIPT_SRC = 'https://cdn.apple-mapkit.com/mk/5.x.x/mapkit.js';

/**
 * The token probe's outcome. `transient` distinguishes a blip we should NOT
 * cache (the 4s race timed out, the network refused, a 5xx) from a definitive
 * "not configured" (a 501 — `MAPKIT_PROVIDER` is off): a transient failure at
 * page-open must not poison the whole session, while a real 501 is stable and
 * worth caching so we don't re-hit the endpoint on every mount.
 */
async function fetchToken(): Promise<{ token: string | null; transient: boolean }> {
  try {
    // Bounded: a hanging token endpoint must not block the lazy chooser —
    // the fallback engine needs no MapKit token, so a slow answer IS the answer.
    const raced = await Promise.race([
      api<{ token: string }>('/me/mapkit-token').then((body) => ({ body }) as const),
      new Promise<{ timeout: true }>((resolve) =>
        setTimeout(() => resolve({ timeout: true }), 4000),
      ),
    ]);
    if ('timeout' in raced) return { token: null, transient: true };
    return { token: raced.body?.token ?? null, transient: false };
  } catch (err) {
    // A 501 is a definitive "not configured" — safe to cache. Anything else
    // (a refused connection, a 5xx, a network blip) is transient and must not
    // lock the session onto the fallback for want of one slow response.
    return { token: null, transient: !(err instanceof ApiError && err.status === 501) };
  }
}

let decision: Promise<boolean> | null = null;

/**
 * Whether the LAST completed probe failed transiently (a 401/timeout/5xx/network
 * blip) rather than definitively (a 501 "not configured"). The chooser reads it
 * to decide whether an unavailable outcome is worth auto-retrying: a transient
 * failure self-heals, a 501 is stable and should land on the card at once. It
 * reflects the most recent probe because a transient failure clears `decision`,
 * so the next `mapkitReady()` re-runs and refreshes this flag.
 */
let lastTransient = false;
export function mapkitLastTransient(): boolean {
  return lastTransient;
}

/**
 * Drop the memoised readiness so the next `mapkitReady()` probes again.
 *
 * The map's "Try again" affordance calls this: without it a single transient
 * failure (a token or CDN response slower than the ceiling, a blip at page-open)
 * caches "unavailable" for the whole session with no way back but a full reload.
 */
export function resetMapkitDecision(): void {
  decision = null;
}

/**
 * True once: token minted, script loaded, mapkit initialised. Memoised so
 * every Map on every screen shares one probe and one script.
 */
export function mapkitReady(): Promise<boolean> {
  decision ??= (async () => {
    const { token, transient } = await fetchToken();
    if (token === null) {
      // Don't let a transient token failure cache "unavailable" for the whole
      // session — clear the memo so a later mount (or an explicit retry) probes
      // afresh. A definitive 501 is left cached, exactly as before.
      lastTransient = transient;
      if (transient) decision = null;
      return false;
    }

    const loaded = await new Promise<boolean>((resolve) => {
      let settled = false;
      const finish = (ok: boolean) => {
        if (settled) return;
        settled = true;
        resolve(ok);
      };
      const script = document.createElement('script');
      script.src = SCRIPT_SRC;
      script.crossOrigin = 'anonymous';
      script.onload = () => finish(true);
      script.onerror = () => finish(false);
      document.head.appendChild(script);
      // A hard ceiling, exactly as google-loader has: a reachable-but-stalled
      // Apple CDN fires neither onload nor onerror until the browser's own
      // multi-minute resource timeout, which would hang the chooser on "Loading
      // the map…" forever and never degrade to Google. MapKit is the PRIMARY
      // engine, so this is the common path, not an edge.
      setTimeout(() => finish(false), 8000);
    });
    if (!loaded || window.mapkit === undefined) {
      // The script failed or the CDN stalled past the ceiling — a transient
      // condition, not a configuration one, so don't cache it: clear the memo
      // and let the next probe try the load again.
      lastTransient = true;
      decision = null;
      return false;
    }

    window.mapkit.init({
      authorizationCallback: (done) => {
        // First call can reuse the probe's token; later calls (expiry) mint
        // fresh. A failed re-mint hands back the stale token — MapKit will
        // retry — rather than throwing inside Apple's callback.
        void fetchToken().then(({ token: fresh }) => done(fresh ?? token));
      },
    });
    lastTransient = false;
    return true;
  })();
  return decision;
}
